import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';

export default {
    name: 'ProductAdditionRequests',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewProductAdditionRequestsUi()) {
            this.logout();
            return;
        }
        await Promise.all([
            this.GetInfo(1),
            this.loadCountries(),
            this.loadManufacturers(),
            this.loadUnitMeasures(),
            this.loadFormularyFromApi('')
        ]);
    },

    beforeDestroy() {
        clearTimeout(this.searchTimer);
        clearTimeout(this.similarTimer);
        clearTimeout(this.formularySearchTimer);
    },

    computed: {
        isReviewer() {
            return this.canReviewProductAdditionRequestsUi();
        },
        isFacilityUser() {
            return this.canCreateProductAdditionRequestsUi();
        },
        mergedFormularyItems() {
            const map = new Map();
            [...(this.formularyItems || []), ...(this.editFormularyExtra || [])].forEach((item) => {
                if (item && item.id != null) map.set(Number(item.id), item);
            });
            return Array.from(map.values());
        },
        selectedFormularyPreview() {
            const id = Number(this.createForm.nationalFormularyItemId);
            if (!id) return null;
            return this.mergedFormularyItems.find((x) => Number(x.id) === id) || null;
        },
        statsCards() {
            return [
                this.statCard('إجمالي الطلبات', this.pages, 'primary', 'ti ti-package'),
                this.statCard('مضافة — قيد المراجعة', this.countWhere(this.Info, item => this.matchStatus(item, [1], ['بانتظار', 'قيد المراجعة'])), 'warning', 'ti ti-clock'),
                this.statCard('روجعت واعتُمدت', this.countWhere(this.Info, item => this.matchStatus(item, [2], ['معتمد'])), 'success', 'ti ti-circle-check'),
                this.statCard('مرفوضة', this.countWhere(this.Info, item => this.matchStatus(item, [3], ['مرفوض'])), 'danger', 'ti ti-ban'),
            ];
        }
    },

    data() {
        return {
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            Search: '',
            statusFilter: 0,
            Info: [],
            detail: null,
            detailDialog: false,
            createDialog: false,
            reviewDialog: false,
            countries: [],
            manufacturers: [],
            unitMeasures: [],
            formularyItems: [],
            editFormularyExtra: [],
            formularyRemoteLoading: false,
            formularySearchTimer: null,
            similarTimer: null,
            searchTimer: null,
            similarCheck: null,
            similarLoading: false,
            createForm: {
                productName: '',
                nationalFormularyItemId: null,
                manufacturerId: null,
                countryRegionId: null,
                batchNumber: '',
                descriptions: '',
                showOnPublicFormulary: false,
                requestNote: '',
                packagings: []
            },
            reviewForm: {
                id: 0,
                approve: true,
                reviewNote: '',
                productName: '',
                nationalFormularyItemId: null,
                manufacturerId: null,
                countryRegionId: null,
                batchNumber: '',
                descriptions: '',
                showOnPublicFormulary: false,
                packagings: []
            }
        };
    },

    methods: {
        statusBadgeClass(status) {
            const s = Number(status);
            if (s === 2) return 'bg-label-success';
            if (s === 3) return 'bg-label-danger';
            if (s === 1) return 'bg-label-warning';
            return 'bg-label-secondary';
        },
        statusLabel(status) {
            const s = Number(status);
            if (s === 1) return 'مضاف — قيد المراجعة';
            if (s === 2) return 'روجع واعتُمد';
            if (s === 3) return 'مرفوض';
            return '-';
        },
        requestCode(item) {
            if (!item) return '-';
            return `PAR-${item.id}`;
        },
        normalizeFormularyList(data) {
            const root = data || {};
            const raw = root.info != null ? root.info : root.Info;
            const arr = Array.isArray(raw) ? raw : [];
            return arr.map((r) => ({
                id: r.id != null ? r.id : r.Id,
                nationalCode: r.nationalCode != null ? r.nationalCode : (r.NationalCode || ''),
                scientificName: r.scientificName != null ? r.scientificName : (r.ScientificName || ''),
                categoryName: r.categoryName != null ? r.categoryName : (r.CategoryName || null),
            })).filter((x) => x.id != null);
        },
        formularyLabel(item) {
            const code = item.nationalCode || '';
            const name = item.scientificName || '';
            return code ? `${name} (${code})` : name;
        },
        async loadFormularyFromApi(search = '') {
            this.formularyRemoteLoading = true;
            try {
                const res = await this.$http.GetNationalFormularyApproved(search, 500);
                this.formularyItems = this.normalizeFormularyList(res.data);
            } catch {
                this.formularyItems = [];
            } finally {
                this.formularyRemoteLoading = false;
            }
        },
        remoteSearchNationalFormulary(query) {
            if (this.formularySearchTimer) clearTimeout(this.formularySearchTimer);
            const q = String(query || '').trim();
            this.formularySearchTimer = setTimeout(() => this.loadFormularyFromApi(q), 280);
        },
        loadCountries() {
            return this.$http.GetAllCountries()
                .then((res) => { this.countries = res.data.info || res.data || []; })
                .catch(() => { this.countries = []; });
        },
        loadManufacturers() {
            return this.$http.GetAllManufacturers()
                .then((res) => { this.manufacturers = res.data.info || res.data || []; })
                .catch(() => { this.manufacturers = []; });
        },
        loadUnitMeasures() {
            return this.$http.GetProductAdditionUnitMeasures()
                .then((res) => { this.unitMeasures = res.data.info || res.data || []; })
                .catch(() => { this.unitMeasures = []; });
        },
        emptyPackagingLine(arrangement) {
            return {
                unitMeasureId: null,
                quantity: 1,
                arrangement: arrangement || 1
            };
        },
        normalizePackagingLines(lines) {
            return (lines || []).map((line, index) => ({
                unitMeasureId: line.unitMeasureId != null ? line.unitMeasureId : line.UnitMeasureId,
                quantity: Number(line.quantity != null ? line.quantity : line.Quantity) || 0,
                arrangement: Number(line.arrangement != null ? line.arrangement : line.Arrangement) || (index + 1)
            }));
        },
        addPackagingLine(target) {
            const form = target === 'review' ? this.reviewForm : this.createForm;
            const nextArrangement = (form.packagings || []).length + 1;
            form.packagings.push(this.emptyPackagingLine(nextArrangement));
        },
        removePackagingLine(target, index) {
            const form = target === 'review' ? this.reviewForm : this.createForm;
            form.packagings.splice(index, 1);
            form.packagings.forEach((line, i) => {
                if (!line.arrangement || line.arrangement <= 0) line.arrangement = i + 1;
            });
        },
        validatePackagingForm(packagings) {
            const lines = this.normalizePackagingLines(packagings);
            if (!lines.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'يجب تحديد عبوة واحدة على الأقل.');
                return null;
            }
            const seen = new Set();
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                if (!line.unitMeasureId) {
                    this.$helper.ShowMessage('warning', 'تنبيه', `اختر وحدة القياس للعبوة رقم ${i + 1}.`);
                    return null;
                }
                if (line.quantity <= 0) {
                    this.$helper.ShowMessage('warning', 'تنبيه', `كمية العبوة يجب أن تكون أكبر من صفر للعبوة رقم ${i + 1}.`);
                    return null;
                }
                if (line.arrangement <= 0) {
                    this.$helper.ShowMessage('warning', 'تنبيه', `ترتيب العرض يجب أن يكون أكبر من صفر للعبوة رقم ${i + 1}.`);
                    return null;
                }
                const key = `${line.unitMeasureId}:${line.arrangement}`;
                if (seen.has(key)) {
                    this.$helper.ShowMessage('warning', 'تنبيه', 'لا يمكن تكرار نفس وحدة القياس وترتيب العرض في العبوات.');
                    return null;
                }
                seen.add(key);
            }
            return lines;
        },
        GetInfo(pageNo) {
            this.pageNo = pageNo || 1;
            this.$blockUI.Start();
            this.$http.GetProductAdditionRequests(this.pageNo, this.pageSize, this.Search, this.statusFilter)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.Info = response.data.info || [];
                    this.pages = response.data.count || 0;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.Info = [];
                    this.pages = 0;
                });
        },
        scheduleSimilarCheck() {
            if (this.similarTimer) clearTimeout(this.similarTimer);
            this.similarTimer = setTimeout(() => this.checkSimilarNames(), 350);
        },
        checkSimilarNames() {
            const name = String(this.createForm.productName || '').trim();
            const formularyId = Number(this.createForm.nationalFormularyItemId);
            if (!name || !formularyId) {
                this.similarCheck = null;
                return;
            }
            this.similarLoading = true;
            this.$http.CheckProductAdditionSimilar(name, formularyId)
                .then((res) => {
                    this.similarCheck = res.data || null;
                })
                .catch(() => {
                    this.similarCheck = null;
                })
                .finally(() => {
                    this.similarLoading = false;
                });
        },
        openCreateDialog() {
            this.createForm = {
                productName: '',
                nationalFormularyItemId: null,
                manufacturerId: null,
                countryRegionId: null,
                batchNumber: '',
                descriptions: '',
                showOnPublicFormulary: false,
                requestNote: '',
                packagings: [this.emptyPackagingLine(1)]
            };
            this.similarCheck = null;
            this.editFormularyExtra = [];
            this.loadFormularyFromApi('');
            this.createDialog = true;
        },
        submitCreate() {
            const name = String(this.createForm.productName || '').trim();
            if (!name || !this.createForm.nationalFormularyItemId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل الاسم التجاري واختر الصنف النمطي.');
                return;
            }
            if (!this.createForm.manufacturerId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر الشركة المصنعة.');
                return;
            }
            if (!this.createForm.countryRegionId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر بلد المنشأ.');
                return;
            }
            if (this.similarCheck && this.similarCheck.hasExactMatch) {
                this.$helper.ShowMessage('error', 'تكرار', 'المنتج موجود مسبقاً في النظام بنفس الاسم والصنف النمطي.');
                return;
            }
            const packagings = this.validatePackagingForm(this.createForm.packagings);
            if (!packagings) return;
            const payload = {
                ...this.createForm,
                packagings
            };
            this.$blockUI.Start();
            this.$http.CreateProductAdditionRequest(payload)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', response.data.message || 'تم إرسال الطلب.');
                    this.createDialog = false;
                    this.GetInfo(1);
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        openDetail(item) {
            this.$blockUI.Start();
            this.$http.GetProductAdditionRequest(item.id)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.detail = response.data.info || null;
                    this.detailDialog = true;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('error', 'خطأ', 'تعذر تحميل التفاصيل.');
                });
        },
        openReview(approve) {
            if (!this.detail) return;
            this.reviewForm = {
                id: this.detail.id,
                approve: !!approve,
                reviewNote: '',
                productName: this.detail.productName || '',
                nationalFormularyItemId: this.detail.nationalFormularyItemId,
                manufacturerId: this.detail.manufacturerId,
                countryRegionId: this.detail.countryRegionId,
                batchNumber: this.detail.batchNumber || '',
                descriptions: this.detail.descriptions || '',
                showOnPublicFormulary: !!this.detail.showOnPublicFormulary,
                packagings: this.normalizePackagingLines(this.detail.packagings).map((line) => ({ ...line }))
            };
            if (!this.reviewForm.packagings.length) {
                this.reviewForm.packagings = [this.emptyPackagingLine(1)];
            }
            if (this.detail.nationalFormularyItemId) {
                this.editFormularyExtra = [{
                    id: Number(this.detail.nationalFormularyItemId),
                    nationalCode: this.detail.nationalCode || '',
                    scientificName: this.detail.scientificName || '',
                    categoryName: this.detail.categoryName || null
                }];
            }
            this.reviewDialog = true;
        },
        submitReview() {
            if (!this.reviewForm.id) return;
            if (!this.reviewForm.approve && !String(this.reviewForm.reviewNote || '').trim()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل سبب الرفض.');
                return;
            }
            if (this.reviewForm.approve && !String(this.reviewForm.productName || '').trim()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الاسم التجاري مطلوب قبل الاعتماد.');
                return;
            }
            if (this.reviewForm.approve && !this.reviewForm.manufacturerId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الشركة المصنعة مطلوبة قبل الاعتماد.');
                return;
            }
            if (this.reviewForm.approve && !this.reviewForm.countryRegionId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'بلد المنشأ مطلوب قبل الاعتماد.');
                return;
            }
            let payload = { ...this.reviewForm };
            if (this.reviewForm.approve) {
                const packagings = this.validatePackagingForm(this.reviewForm.packagings);
                if (!packagings) return;
                payload.packagings = packagings;
            }
            this.$blockUI.Start();
            this.$http.ReviewProductAdditionRequest(payload)
                .then((response) => {
                    this.$blockUI.Stop();
                    const msg = response.data.message || (this.reviewForm.approve ? 'تم الاعتماد.' : 'تم الرفض.');
                    this.$helper.ShowMessage('success', 'تم', msg);
                    this.reviewDialog = false;
                    this.detailDialog = false;
                    this.GetInfo(this.pageNo);
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        }
    }
};
